import { useState, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLocalizedExpertBios } from "@/hooks/useLocalizedExpertBios";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { db } from "@/lib/supabaseQuery";
import { INTERNATIONAL_LOCATION_ID, ORPHAN_SENTINEL_UUID } from "@/constants/config";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PublicationCardSkeleton } from "@/components/skeletons";
import { Button } from "@/components/ui/button";
import { useLocalizations } from "@/hooks/useLocalizations";
import { useAuth } from "@/contexts/AuthContext";
import { usePrimaryLocale } from "@/hooks/usePrimaryLocale";
import { useLocale } from "@/contexts/LocaleContext";
import { LPages } from "@/components/i18n";
import { PublicationCard } from "@/components/PublicationCard";
import { LocationDescriptionCard } from "@/components/sections/LocationDescriptionCard";
import {
  usePublicationsWithAuthors,
  useExpertFeaturedPublications,
  useRecentHumanPublications,
  type PublicationWithAuthor,
} from "@/hooks/usePublicationsWithAuthors";
import {
  fetchExpertsByLocationWithPriority,
  fetchGlobalExpertsWithLocationDiversity,
} from "@/utils/expertPriorityQuery";
import { Globe, PenLine, Tag, Users } from "lucide-react";
import { RESPONSIVE, LAYOUTS, TYPOGRAPHY, SPACING, ICONS } from "@/design-system";
import { cn, capitalizeWords, sanitizeBio } from "@/lib/utils";
import { useLocalizedRoutes } from "@/hooks/useLocalizedRoutes";
import { ROUTES } from "@/constants/routes";
import { getSmartAuthRoute } from "@/utils/authRedirect";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const ITEMS_PER_PAGE = 10;
const ARTICLE_CANDIDATE_LIMIT = 50;
const EXPERTS_PILL_KEY = 'experts';
const SIDEBAR_WIDGET_PUBLICATION_THRESHOLD = ITEMS_PER_PAGE;

interface TopicPill {
  key: string;
  label: string;
  keywords: string[];
}

interface HomeInsightsSectionProps {
  locationId: string | null;
  locationName?: string;
  locationCountryCode?: string | null;
  locationDescription?: string | null;
  variant?: 'home' | 'explore';
}

interface RankedExpert {
  id: string;
  display_name: string | null;
  vanity_url: string | null;
  image: string | null;
  country: string | null;
  location_id: string | null;
  membership_tier: string | null;
  global_rank: number | null;
  about: string | null;
}

const ExpertItem = ({ expert, bio }: { expert: RankedExpert; bio?: string | null }) => {
  const profileUrl = expert.vanity_url ? `/${expert.vanity_url}` : '#';
  const initials = (expert.display_name || '?').charAt(0).toUpperCase();

  return (
    <Link to={profileUrl} className="flex items-center gap-3 group">
      <Avatar className="h-10 w-10 shrink-0">
        <AvatarImage src={expert.image || undefined} alt={expert.display_name || ''} />
        <AvatarFallback className="bg-muted text-muted-foreground text-sm">
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <h4 className={cn(
          TYPOGRAPHY.cardTitleSmall,
          'truncate group-hover:text-primary transition-colors'
        )}>
          {capitalizeWords(expert.display_name) || 'Unknown'}
        </h4>
        {bio && (
          <p className={cn(TYPOGRAPHY.caption, 'line-clamp-2 text-muted-foreground')}>
            {sanitizeBio(bio)}
          </p>
        )}
      </div>
    </Link>
  );
};

export const HomeInsightsSection = ({
  locationId,
  locationName,
  locationCountryCode,
  locationDescription,
  variant = 'home',
}: HomeInsightsSectionProps) => {
  const { lPages } = useLocalizations();
  const { user } = useAuth();
  const { settings } = useLocale();
  const { resolveToPrimary } = usePrimaryLocale();
  const contentLocale = resolveToPrimary(settings.locale);
  const navigate = useNavigate();
  const { getPath } = useLocalizedRoutes();

  const [selectedTopic, setSelectedTopic] = useState(EXPERTS_PILL_KEY);

  const isInternational = useMemo(() => {
    return !locationId || locationId === INTERNATIONAL_LOCATION_ID || locationId === 'GLOBAL';
  }, [locationId]);

  const locationIdStr = useMemo(() => {
    if (isInternational) return null;
    return locationId;
  }, [locationId, isInternational]);

  const isExpertsPill = selectedTopic === EXPERTS_PILL_KEY;

  // ─── Data fetching ──────────────────────────────────────────────────────

  const { data: locationFilteredTopics } = useQuery({
    queryKey: ['all-topics-localized', contentLocale],
    queryFn: async () => {
      // Fetch topics
      const topics = await db.fetchAll<{
        id: string; name: string; keywords: string[] | null;
        publication_count: number; scope: string; location_id: string | null;
      }>('topics', 'id, name, keywords, publication_count, scope, location_id', {
        filters: [{ column: 'is_active', operator: 'eq', value: true }],
        orderBy: { column: 'publication_count', ascending: false },
      });

      if (contentLocale === 'en') return (topics || []).map(t => ({ ...t, localizedName: t.name }));

      // Fetch localized names for the current locale
      const localizedMeta = await db.fetchAll<{
        source_id: string; translated_name: string | null;
      }>('localization_metadata', 'source_id, translated_name', {
        filters: [
          { column: 'source_type', operator: 'eq', value: 'topic' },
          { column: 'locale', operator: 'eq', value: contentLocale },
        ],
      });

      const nameMap = new Map(
        (localizedMeta || [])
          .filter(m => m.translated_name)
          .map(m => [m.source_id, m.translated_name!])
      );

      return (topics || []).map(t => ({
        ...t,
        localizedName: nameMap.get(t.id) || t.name,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  // Experts pill + International: featured publications from top expert per location
  const { data: expertFeaturedPublications, isLoading: isExpertFeaturedLoading } = useExpertFeaturedPublications({
    limit: 20,
    enabled: isExpertsPill && isInternational,
  });

  // Experts pill: recent human publications (backfill for international, primary for local)
  const { data: recentHumanPublications, isLoading: isRecentHumanLoading } = useRecentHumanPublications({
    limit: ARTICLE_CANDIDATE_LIMIT,
    locationId: locationIdStr,
    enabled: isExpertsPill,
  });

  // Standard fetch including sentinel — always enabled so topic pills can compute visibility
  const { data: standardPublications, isLoading: isStandardLoading } = usePublicationsWithAuthors({
    locationId: locationIdStr,
    limit: ARTICLE_CANDIDATE_LIMIT,
    diversityMode: 'none',
    allowGlobalFallback: isInternational,
  });

  const { data: rankedExperts } = useQuery({
    queryKey: ['home-ranked-experts', locationIdStr, isInternational],
    queryFn: async () => {
      if (isInternational) {
        const experts = await fetchGlobalExpertsWithLocationDiversity(supabase, 10);
        return (experts || []) as RankedExpert[];
      }

      const experts = await fetchExpertsByLocationWithPriority(supabase, {
        locationId: locationIdStr!,
        limit: 10,
        includeGlobalFallback: false,
      });

      return (experts || []) as RankedExpert[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Sidebar bios follow the shared order: visitor's language → English → original.
  const sidebarExpertIds = useMemo(() => (rankedExperts || []).map((e) => e.id), [rankedExperts]);
  const { getBioWithFallback: getSidebarBio } = useLocalizedExpertBios(sidebarExpertIds);

  // ─── Build publication list ─────────────────────────────────────────────────

  const hasLocalPublications = isInternational || (standardPublications?.length ?? 0) > 0;
  const hasExpertPublications = isInternational || (recentHumanPublications?.length ?? 0) > 0;
  const activeTopic = !isInternational
    && selectedTopic === EXPERTS_PILL_KEY
    && !isRecentHumanLoading
    && hasLocalPublications
    && !hasExpertPublications
      ? 'all'
      : selectedTopic;
  const isActiveExpertsPill = activeTopic === EXPERTS_PILL_KEY;

  const browsePublications = useMemo(() => {
    if (isActiveExpertsPill) {
      if (isInternational) {
        // Featured publications (1 per location, top expert) shown first
        const featured = expertFeaturedPublications || [];
        const featuredIds = new Set(featured.map((a) => a.id));

        // Backfill with recent human publications not already shown
        const backfill = (recentHumanPublications || []).filter((a) => !featuredIds.has(a.id));

        return [...featured, ...backfill];
      }

      // Local experts: recent human publications for this location
      return recentHumanPublications || [];
    }

    // Non-experts pills: standard publications (includes sentinel)
    // Topic pills SORT matching publications to the top, then backfill with remaining publications
    const pool = standardPublications || [];

    if (activeTopic === 'all') {
      return pool;
    }

    // Sort: matching topic publications first, then the rest
    const matching: PublicationWithAuthor[] = [];
    const rest: PublicationWithAuthor[] = [];
    for (const a of pool) {
      if (a.topicIds.includes(activeTopic)) {
        matching.push(a);
      } else {
        rest.push(a);
      }
    }

    return [...matching, ...rest];
  }, [
    isActiveExpertsPill, isInternational, expertFeaturedPublications, recentHumanPublications,
    standardPublications, activeTopic,
  ]);

  // ─── Pill visibility: hide topic pills with no content ──────────────────

  const visibleTopicPills = useMemo<TopicPill[]>(() => {
    const expertsPill: TopicPill = {
      key: EXPERTS_PILL_KEY,
      label: lPages('insights.topics.experts', { defaultValue: 'Experts' }),
      keywords: [],
    };
    const allPill: TopicPill = {
      key: 'all',
      label: lPages('insights.topics.all', { defaultValue: 'All' }),
      keywords: [],
    };

    const topics = locationFilteredTopics;
    if (!topics || topics.length === 0) {
      return [
        ...(hasExpertPublications ? [expertsPill] : []),
        ...((standardPublications?.length ?? 0) > 0 ? [allPill] : []),
      ];
    }

    // Determine which topic IDs have content in the standard pool
    const pool = standardPublications || [];
    const topicIdsWithContent = new Set<string>();
    for (const publication of pool) {
      for (const tid of publication.topicIds) {
        topicIdsWithContent.add(tid);
      }
    }

    const filteredTopics = topics
      .filter((topic) => topicIdsWithContent.has(topic.id))
      .map((topic) => ({
        key: topic.id,
        label: topic.localizedName || topic.name,
        keywords: topic.keywords || [],
      }));

    return [
      ...(hasExpertPublications ? [expertsPill] : []),
      ...(pool.length > 0 ? [allPill] : []),
      ...filteredTopics,
    ];
  }, [locationFilteredTopics, lPages, standardPublications, contentLocale, hasExpertPublications]);

  // ─── Loading state ──────────────────────────────────────────────────────

  const isLoading = isActiveExpertsPill
    ? (isInternational ? isExpertFeaturedLoading || isRecentHumanLoading : isRecentHumanLoading)
    : isStandardLoading;
  const isLocationAvailabilityLoading = !isInternational && isStandardLoading;

  // ─── Handlers ───────────────────────────────────────────────────────────

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const handleTopicChange = (topic: string) => {
    setSelectedTopic(topic);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  }, []);

  const handlePublishClick = () => {
    if (user) {
      navigate(ROUTES.ACCOUNT_PUBLICATIONS_NEW);
      return;
    }

    navigate(getSmartAuthRoute(ROUTES.ACCOUNT_PUBLICATIONS_NEW));
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  const renderPublications = () => {
    return browsePublications.slice(0, visibleCount).map((publication) => {
      const author = publication.users;

      return (
        <PublicationCard
          key={publication.id}
          id={publication.id}
          slug={publication.slug}
          slugEn={publication.slugEn}
          localizedSlug={publication.localizedSlug}
          title={publication.localizedTitle ?? publication.title}
          excerpt={publication.localizedExcerpt || publication.excerpt || publication.body || ''}
          featuredImage={publication.featured_image_url || undefined}
          authorId={publication.author_id}
          authorName={author?.display_name ?? author?.author_name ?? lPages('insights.labels.author', { defaultValue: 'Author' })}
          authorVanityUrl={author?.vanity_url}
          authorAvatarUrl={author?.image}
          authorCountry={author?.country}
          publishedAt={publication.published_at}
          topicIds={publication.topicIds}
          variant="horizontal"
        />
      );
    });
  };

  const hasPublications = browsePublications.length > 0;
  const showSidebarWidgets = (standardPublications?.length ?? 0) >= SIDEBAR_WIDGET_PUBLICATION_THRESHOLD;

  const verifiedExpertsSidebar = (
    <aside className="lg:col-span-1">
      <div className="sticky top-20 sm:top-24 space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className={cn(RESPONSIVE.flexRowCenterGap2, 'text-lg')}>
              <Users className={cn(ICONS.sectionHeader, 'text-primary')} />
              <LPages id="insights.sidebar.verified_experts" defaultValue="Verified Experts" />
            </CardTitle>
          </CardHeader>
          <CardContent className={cn(SPACING.contentContainer)}>
            {rankedExperts && rankedExperts.length > 0 ? (
              rankedExperts.map((expert) => (
                <ExpertItem key={expert.id} expert={expert} bio={getSidebarBio(expert.id, expert.about)} />
              ))
            ) : (
              <div className="space-y-3">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="flex items-center gap-3 animate-pulse">
                    <div className="h-10 w-10 rounded-full bg-muted" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-muted rounded w-3/4" />
                      <div className="h-2 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {showSidebarWidgets && (
          <>
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className={cn(SPACING.cardContent, SPACING.stackLg, SPACING.cardPadding)}>
                <div className={RESPONSIVE.flexRowCenterGap2}>
                  <Users className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-base">
                    <LPages id="insights.sidebar.find_expert.title" defaultValue="Find an Expert" />
                  </h3>
                </div>
                <p className={TYPOGRAPHY.cardDescription}>
                  <LPages id="insights.sidebar.find_expert.tagline" defaultValue="Connect with tax professionals worldwide" />
                </p>
                <Button asChild className="w-full">
                  <Link to={getPath('experts')}>
                    <LPages id="insights.sidebar.find_expert.button" defaultValue="Browse Experts" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className={cn(SPACING.cardContent, SPACING.stackLg, SPACING.cardPadding)}>
                <div className={RESPONSIVE.flexRowCenterGap2}>
                  <PenLine className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-base">
                    <LPages id="insights.sidebar.publish.title" defaultValue="Publish New" />
                  </h3>
                </div>
                <p className={TYPOGRAPHY.cardDescription}>
                  <LPages id="insights.sidebar.publish.tagline" defaultValue="Share your expertise with the network" />
                </p>
                <Button onClick={handlePublishClick} className="w-full">
                  <LPages id="insights.sidebar.publish.button" defaultValue="Start Writing" />
                </Button>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className={cn(SPACING.cardContent, SPACING.stackLg, SPACING.cardPadding)}>
                <div className={RESPONSIVE.flexRowCenterGap2}>
                  <Globe className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-base">
                    <LPages id="insights.sidebar.strategies.title" defaultValue="Top International Strategies" />
                  </h3>
                </div>
                <p className={TYPOGRAPHY.cardDescription}>
                  <LPages id="insights.sidebar.strategies.tagline" defaultValue="AI & Data-Driven Planning" />
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to={getPath('strategies')}>
                    <LPages id="insights.sidebar.strategies.button" defaultValue="View Strategies" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className={cn(SPACING.cardContent, SPACING.stackLg, SPACING.cardPadding)}>
                <div className={RESPONSIVE.flexRowCenterGap2}>
                  <Tag className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-base">
                    <LPages id="insights.sidebar.topics.title" defaultValue="Explore Topics" />
                  </h3>
                </div>
                <p className={TYPOGRAPHY.cardDescription}>
                  <LPages id="insights.sidebar.topics.tagline" defaultValue="Browse by tax category" />
                </p>
                <Button asChild variant="outline" className="w-full">
                  <Link to={getPath('topics')}>
                    <LPages id="insights.sidebar.topics.button" defaultValue="View Topics" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </aside>
  );

  const insightsContent = (
    <>
      <div className={LAYOUTS.sidebarLayoutSpaced}>
        <div className={cn(LAYOUTS.sidebarMainNoSpacing, SPACING.sectionContainer)}>
          <div className="min-h-[44px]">
            <div className="relative overflow-hidden">
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10" />
              <div className="flex gap-2 overflow-x-auto scrollbar-hide px-1 py-1 -mx-1 pr-16">
                {visibleTopicPills.map((topic) => (
                  <Button
                    key={topic.key}
                    variant={activeTopic === topic.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleTopicChange(topic.key)}
                    className="rounded-full whitespace-nowrap flex-shrink-0"
                  >
                    {topic.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col gap-4">
              {[...Array(4)].map((_, index) => (
                <PublicationCardSkeleton key={index} variant="horizontal" />
              ))}
            </div>
          ) : (
            <div className={SPACING.contentContainer}>
              {hasPublications && (
                <div className="flex flex-col gap-4 sm:gap-5">
                  {renderPublications()}
                </div>
              )}
              {locationName && !showSidebarWidgets && (
                <LocationDescriptionCard
                  locationName={locationName}
                  countryCode={locationCountryCode}
                  description={locationDescription}
                />
              )}
              {browsePublications.length > visibleCount && (
                <div className="mt-6 flex justify-center">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleLoadMore}
                  >
                    <LPages id="insights.load_more" defaultValue="Load More" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {verifiedExpertsSidebar}
      </div>
    </>
  );

  if (variant === 'explore') {
    return insightsContent;
  }

  if (isLocationAvailabilityLoading) {
    return (
      <section className={cn('bg-background', RESPONSIVE.sectionPy)}>
        <div className="container max-w-4xl">
          <PublicationCardSkeleton variant="horizontal" />
        </div>
      </section>
    );
  }

  if (!isInternational && !hasLocalPublications) {
    return (
      <section className={cn('bg-background', RESPONSIVE.sectionPy)}>
        <div className="container">
          <div className={LAYOUTS.sidebarLayoutSpaced}>
            <div className={cn(LAYOUTS.sidebarMainNoSpacing, SPACING.sectionContainer)}>
              {locationName && (
                <LocationDescriptionCard
                  locationName={locationName}
                  countryCode={locationCountryCode}
                  description={locationDescription}
                />
              )}
            </div>
            {verifiedExpertsSidebar}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn('bg-background', RESPONSIVE.sectionPy)}>
      <div className="container">
        <div className={cn('text-center', RESPONSIVE.marginBottomSm)}>
          <h2 className={cn(RESPONSIVE.titleSection)}>
            <LPages id="home.insights.title" defaultValue="Latest Insights" />
          </h2>
          <p className={cn(RESPONSIVE.subtitleSection)}>
            <LPages id="home.insights.subtitle" defaultValue="Expert commentary and analysis from our network" />
          </p>
        </div>

        {insightsContent}
      </div>
    </section>
  );
};
